// SSE progress payload helpers — shared across all onProgress sites.
//
// progressPayload(stage, progress, step, data) resolves the backend (stage, step)
// to a canonical step via progressSteps and returns the {stage, step, stepIdx, pct,
// message} object runProgress() expects. `message` is the "Step N/X — <label>" line
// (no percentage); `pct` is still computed (single normalizePct call) but carried
// for persistence only, not shown. `stepIdx` is -1 for an unrecognized phase.
//
// makeProgressHandler(dispatch, runId) returns the `({stage, step, progress, data})=>`
// callback for sites whose handler body IS the dispatch. Unknown phases (stepIdx < 0)
// are SKIPPED so the running line holds its current step instead of blanking to the
// fallback. Sites with their own guard call progressPayload() for the literal only.

import { normalizePct } from "../stageLabel";
import { runProgress } from "./reducer.js";
import {
  ladderForTag,
  progressStepIndex,
  formatProgressStep,
  introStepsForTag,
} from "./progressSteps.js";
import { laneTier } from "./engines.js";

/**
 * Paced synthetic "intro" for the opening ladder steps at forecast start. The backend
 * blows through Preprocessing/Queued/Planning in <1s so they never render and the line
 * lands straight on Researching. This dispatches those early steps on a short timer so
 * each is briefly visible, then hands off. A caller routes its real onProgress through
 * `defer(apply)`: while the intro is active the frame is HELD (only the latest is kept)
 * and applied once at hand-off, so a fast first frame isn't lost; after hand-off `defer`
 * returns false and the caller applies the frame itself. Cosmetic + time based;
 * monotonic-safe (the reducer max()es on stepIdx and the intro only climbs).
 * ALWAYS `stop()` it (in the submit finally) to clear timers; stop() also discards any
 * held frame, so nothing dispatches after a cancel or completion.
 *
 * @param {Function} dispatch  e.g. dispatchRun
 * @param {string} runId
 * @param {string|null} engine  the run's forecaster name → tier → ladder
 * @param {{ dwellMs?: number, setTimeoutFn?: Function, clearTimeoutFn?: Function }} [opts]
 * @returns {{ active: () => boolean, defer: (apply: () => void) => boolean, stop: () => void }}
 */
export function startProgressIntro(dispatch, runId, engine, opts = {}) {
  const dwellMs = opts.dwellMs ?? 850;
  const setT = opts.setTimeoutFn || setTimeout;
  const clearT = opts.clearTimeoutFn || clearTimeout;
  const steps = introStepsForTag(laneTier(engine));
  let active = steps.length > 0;
  let idx = 0;
  let held = null;
  const timers = [];
  const handOff = () => {
    active = false;
    const apply = held;
    held = null;
    apply?.();
  };
  const fire = () => {
    if (idx >= steps.length) return;
    const s = steps[idx++];
    dispatch(
      runProgress(runId, {
        stage: null,
        step: null,
        stepIdx: s.stepIdx,
        pct: null,
        message: s.message,
      }),
    );
    // Schedule the next step, or — after the last — hold it one dwell then hand off.
    timers.push(setT(idx < steps.length ? fire : handOff, dwellMs));
  };
  if (active) fire();
  return {
    active: () => active,
    defer: (apply) => {
      if (!active) return false;
      held = apply;
      return true;
    },
    stop: () => {
      active = false;
      held = null;
      timers.forEach((t) => clearT(t));
    },
  };
}

/**
 * Build the progress payload dispatched to the run store. The user-facing
 * `message` is the canonical "Step N/X — <label>" line (no percentage), resolved
 * from the backend (stage, step) against the ENGINE'S ladder (quick vs deep — see
 * progressSteps); `pct` is still carried for persistence but no longer rendered.
 * `stepIdx` (or -1 for an unrecognized phase) lets the reducer enforce monotonic,
 * never-regress step advancement. `stepIdx` is ladder-relative, so a run must use
 * one engine's ladder throughout (it does — the engine is fixed per run).
 *
 * @param {string|null} stage
 * @param {number|null} progress  Raw progress value (0-1 fraction or 0-100 int)
 * @param {string|null} [step]    Backend fine-step name (plan_built/agent_turn/…)
 * @param {object|null} [data]    Progress frame `data` blob (turn/max_turns, …)
 * @param {string|null} [engine]  The run's forecaster name → tag → ladder (default deep)
 * @returns {{ stage: string|null, step: string|null, stepIdx: number, pct: number|null, message: string|null }}
 */
export function progressPayload(stage, progress, step = null, data = null, engine = null) {
  const ladder = ladderForTag(laneTier(engine));
  const pct = normalizePct(progress);
  // pct is consulted only by the quick ladder (buckets its climbing counter into
  // synthetic phases); the deep ladder ignores it.
  const stepIdx = progressStepIndex(ladder, stage, step, pct);
  return {
    stage,
    step: step ?? null,
    stepIdx,
    pct,
    message: stepIdx >= 0 ? formatProgressStep(ladder, stepIdx, data) : null,
  };
}

/**
 * Return an `({stage, step, progress, data})=>` handler that dispatches a
 * runProgress action for the given run + engine. Unrecognized phases (stepIdx < 0)
 * are skipped so the line holds its current step rather than blanking to the fallback.
 *
 * @param {Function} dispatch  e.g. dispatchRun
 * @param {string}   runId
 * @param {string|null} [engine]  The run's forecaster name (selects quick/deep ladder)
 * @returns {(frame: {stage?: string|null, step?: string|null, progress?: number|null, data?: object|null}) => void}
 */
export function makeProgressHandler(dispatch, runId, engine = null) {
  return ({ stage, step, progress, data }) => {
    const payload = progressPayload(stage, progress, step, data, engine);
    if (payload.stepIdx < 0) return; // unknown phase — keep the current step
    dispatch(runProgress(runId, payload));
  };
}
